/**
 * PreferenceSchemaInline - Slice I.a
 *
 * Reusable read+edit preference grid that mirrors EXACTLY the rendering of
 * /me Preferences (DefaultsTab). Used by PlanModals so the plan-a-service
 * popup looks and feels identical to /me preferences. Schema comes from
 * `/api/me/preferences/schema` (admin-canonical via db.categories), so any
 * change in admin Master Preferences shows here automatically.
 *
 * Props:
 *   categoryId   - only render this category's sections + fields
 *   value        - { [field.key]: ['option_id', 'option_id', ...] }
 *   onChange(v)  - receives the full updated `value` object
 *   testidPrefix - optional, defaults to 'plan-pref'
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronRight, Check, X, Image as ImageIcon, Loader2, Plus, Upload, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PreferenceSchemaInline({ categoryId, value = {}, onChange, testidPrefix = "plan-pref" }) {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSections, setOpenSections] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem("session_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await fetch(`${API}/me/preferences/schema`, { headers });
        if (!r.ok) throw new Error(`${r.status}`);
        const d = await r.json();
        if (alive) setSchema(d);
      } catch {
        if (alive) setSchema(null);
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const targetCategory = useMemo(() => {
    if (!schema) return null;
    const want = String(categoryId || "").replace(/^cat_/, "").toLowerCase();
    return (schema.categories || []).find((c) => String(c.category_id || c.key || "").toLowerCase().replace(/^cat_/, "") === want);
  }, [schema, categoryId]);

  if (loading) {
    return <div className="flex items-center gap-2 text-xs text-stone-400 py-3"><Loader2 className="w-3 h-3 animate-spin" /> Loading preferences…</div>;
  }
  if (!targetCategory) {
    return <p className="text-xs text-stone-400">No preferences configured for this category yet.</p>;
  }

  // Iter 9.47.q fix - schema returns `preference_fields` (flat). Group them by section_label.
  const fieldsAll = (targetCategory.preference_fields || targetCategory.fields || []).filter(
    (f) => f.default_visible !== false && f.hidden_in_category_modal !== true,
  );

  if (fieldsAll.length === 0) {
    return <p className="text-xs text-stone-400">No preferences configured for this category yet.</p>;
  }

  // Group by section_label. Fields without a section_label fall under "general".
  const grouped = [];
  const sectionMap = new Map();
  for (const f of fieldsAll) {
    const sid = f.section || f.section_label || "_general";
    const sLabel = f.section_label || "";
    if (!sectionMap.has(sid)) {
      const entry = { sid, section_label: sLabel, fields: [] };
      sectionMap.set(sid, entry);
      grouped.push(entry);
    }
    sectionMap.get(sid).fields.push(f);
  }
  const sections = grouped;

  const setFieldValue = (fieldKey, next) => {
    const v = { ...(value || {}) };
    v[fieldKey] = next;
    onChange && onChange(v);
  };

  const toggleSection = (sid) => setOpenSections((p) => ({ ...p, [sid]: !p[sid] }));

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-grid`}>
      {sections.map((section) => {
        const sid = section.sid;
        const fields = section.fields || [];
        if (fields.length === 0) return null;
        const startsOpen = openSections[sid] !== false; // open by default
        const labeled = !!section.section_label;
        return (
          <div key={sid} data-testid={`${testidPrefix}-section-${sid}`} className="space-y-2">
            {labeled && (
              <button
                type="button"
                onClick={() => toggleSection(sid)}
                className="flex items-center gap-2 w-full text-left group"
                data-testid={`${testidPrefix}-section-toggle-${sid}`}
              >
                <ChevronRight className={`w-3 h-3 text-stone-400 transition-transform ${startsOpen ? "rotate-90" : ""}`} />
                <h4 className="text-[10px] font-semibold tracking-wider uppercase text-stone-600">{section.section_label}</h4>
                <span className="text-[10px] text-stone-300">·</span>
                <span className="text-[10px] text-stone-400">{fields.length}</span>
                <div className="flex-1 h-px bg-stone-100" />
              </button>
            )}
            {startsOpen && (
              <div className="space-y-3 pl-1">
                {fields.map((f) => (
                  <FieldRow
                    key={f.key || f.field_id}
                    field={f}
                    value={value[f.key || f.field_id]}
                    onChange={(v) => setFieldValue(f.key || f.field_id, v)}
                    testidPrefix={testidPrefix}
                    onProductPicked={(product) => {
                      // Iter 9.50+ R4: foundation product picked -> auto-fill
                      // linked generic field (e.g. foundation_shade -> foundation_shade_generic).
                      // Read from f.auto_fills (canonical schema list) OR
                      // legacy aliases linked_generic_field / generic_field_link.
                      const autoFills = Array.isArray(f.auto_fills) ? f.auto_fills : (f.linked_generic_field ? [f.linked_generic_field] : (f.generic_field_link ? [f.generic_field_link] : []));
                      const hint = product?.generic_shade_hint;
                      if (autoFills.length && hint) {
                        for (const linkedKey of autoFills) {
                          const existing = (value || {})[linkedKey];
                          if (!existing) setFieldValue(linkedKey, hint);
                        }
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FieldRow({ field, value, onChange, testidPrefix, onProductPicked }) {
  const t = String(field.type || field.field_type || "multi_select").toLowerCase();
  const displayMode = String(field.display_mode || "").toLowerCase();

  // Iter 9.49+ - Universal pop-up fields (allergies, sensitivities, accessibility)
  // render as a tappable summary that opens its own full pop-up dialog.
  if (displayMode === "popup" || field.is_universal) {
    return <UniversalPopupTrigger field={field} value={value} onChange={onChange} testidPrefix={testidPrefix} />;
  }

  // Iter 9.49+ - product picker: shows real product cards (image + name)
  // sourced from /api/master-products filtered by field.product_filter.
  if (t === "product_search" || displayMode === "product_search" || displayMode === "product_search_aggregated") {
    return <ProductPicker field={field} value={value} onChange={onChange} testidPrefix={testidPrefix} onProductPicked={onProductPicked} />;
  }

  // Iter 9.49+ - inspo grid: opens a folder pop-up sourced from
  // /api/inspo-folders?category=...
  if (displayMode === "image_grid_inspo" || field.inspo_folder_category) {
    return <InspoGridPicker field={field} value={value} onChange={onChange} testidPrefix={testidPrefix} />;
  }

  if (t === "multi_select" || t === "multi_select_master" || t === "multi_select_brand" || t === "swatches" || t === "swatches_text" || t === "image_grid") {
    return <MultiSelectGrid field={field} value={value} onChange={onChange} testidPrefix={testidPrefix} />;
  }
  if (t === "single_select") {
    const arr = value ? [value] : [];
    return (
      <MultiSelectGrid
        field={field}
        value={arr}
        onChange={(next) => onChange(next.length ? next[next.length - 1] : "")}
        testidPrefix={testidPrefix}
      />
    );
  }
  if (t === "text" || t === "textarea" || t === "notes") {
    return (
      <div data-testid={`${testidPrefix}-text-${field.key}`}>
        <label className="text-xs font-medium text-stone-700 block mb-1">{field.label}</label>
        {field.help && <p className="text-[10px] text-stone-500 mb-1">{field.help}</p>}
        <textarea
          rows={2}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-xs border border-stone-200 rounded-md p-2 focus:outline-none focus:border-purple-400"
        />
      </div>
    );
  }
  return null;
}

function MultiSelectGrid({ field, value = [], onChange, testidPrefix }) {
  const normalized = Array.isArray(value) ? value : value ? [value] : [];
  const toggle = (id) => {
    if (normalized.includes(id)) onChange(normalized.filter((v) => v !== id));
    else onChange([...normalized, id]);
  };
  const enabled = (field.options || []).filter((o) => o.enabled !== false);
  const hasImages = enabled.some((o) => o.image_url);
  const hasSwatches = !hasImages && enabled.some((o) => o.swatch_color);

  return (
    <div className="space-y-1.5" data-testid={`${testidPrefix}-field-${field.key}`}>
      <label className="text-xs font-medium text-stone-700">{field.label}</label>
      {(field.help || field.help_text) && <p className="text-[10px] text-stone-500">{field.help || field.help_text}</p>}
      <div className={hasImages ? "grid grid-cols-3 sm:grid-cols-4 gap-2" : "flex flex-wrap gap-1.5"}>
        {enabled.map((opt) => {
          const selected = normalized.includes(opt.id);
          if (hasImages) {
            const isNone = String(opt.id).toLowerCase() === "none";
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggle(opt.id)}
                title={opt.tooltip || opt.label}
                data-testid={`${testidPrefix}-option-${field.key}-${opt.id}`}
                className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                  selected ? "border-stone-900 shadow-md" : "border-stone-200 hover:border-stone-400"
                }`}
              >
                <div
                  className="aspect-square flex items-center justify-center"
                  style={
                    isNone ? { background: "#f9fafb" } :
                    opt.swatch_color ? { background: opt.swatch_color } : { background: "#f3f4f6" }
                  }
                >
                  {isNone ? <X className="w-5 h-5 text-stone-400" /> :
                   opt.image_url ? <img src={opt.image_url} alt={opt.label} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> :
                   !opt.swatch_color ? <ImageIcon className="w-4 h-4 text-stone-400" /> : null}
                </div>
                <div className="px-1.5 py-1 text-[11px] font-medium text-stone-900 text-left truncate">{opt.label}</div>
                {selected && <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-green-500 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-white" /></div>}
              </button>
            );
          }
          if (hasSwatches && opt.swatch_color) {
            // Iter 9.49+ - half-and-half swatch (e.g. undertone cool=blue+purple)
            const swatchBg = Array.isArray(opt.swatch_split) && opt.swatch_split.length === 2
              ? `linear-gradient(90deg, ${opt.swatch_split[0]} 50%, ${opt.swatch_split[1]} 50%)`
              : opt.swatch_color;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggle(opt.id)}
                title={opt.tooltip || opt.label}
                data-testid={`${testidPrefix}-option-${field.key}-${opt.id}`}
                className={`flex items-center gap-1.5 pl-1 pr-2.5 py-0.5 rounded-full text-[11px] font-medium border-2 transition-colors ${
                  selected ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                }`}
              >
                <span className="w-4 h-4 rounded-full border border-stone-300 flex-shrink-0" style={{ background: swatchBg }} />
                {opt.label}
                {selected && <Check className="w-3 h-3 ml-0.5" />}
              </button>
            );
          }
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => toggle(opt.id)}
              title={opt.tooltip || opt.label}
              data-testid={`${testidPrefix}-option-${field.key}-${opt.id}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                selected ? "bg-stone-900 text-white" : "bg-stone-50 text-stone-700 hover:bg-stone-100 border border-stone-200"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}


/* ============================================================
 * Iter 9.49+ - New field renderers per user spec (Feb 2026)
 * R6 - All pop-up triggers share a global open key so opening one
 *      auto-closes any other open picker. Fixes the "they pile on
 *      top of each other where I have to X out" bug.
 * ============================================================ */
let __activePopupCloser = null;
function _registerActivePopup(closeFn) {
  if (__activePopupCloser && __activePopupCloser !== closeFn) {
    try { __activePopupCloser(); } catch { /* noop */ }
  }
  __activePopupCloser = closeFn;
}
function _releaseActivePopup(closeFn) {
  if (__activePopupCloser === closeFn) __activePopupCloser = null;
}

function UniversalPopupTrigger({ field, value = [], onChange, testidPrefix }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null);
  const popupKey = field.popup_source || field.field_id || field.key;
  const normalized = Array.isArray(value) ? value : value ? [value] : [];

  // Iter 9.49+ R6 - only one preference popup may be open at a time.
  useEffect(() => {
    if (open) {
      const closer = () => setOpen(false);
      _registerActivePopup(closer);
      return () => _releaseActivePopup(closer);
    }
  }, [open]);

  useEffect(() => {
    if (!open || options !== null) return;
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem("session_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await fetch(`${API}/universal-preferences/${popupKey}`, { headers });
        if (r.ok) {
          const d = await r.json();
          if (alive) setOptions(d.options || []);
        } else if (alive) setOptions([]);
      } catch {
        if (alive) setOptions([]);
      }
    })();
    return () => { alive = false; };
  }, [open, popupKey, options]);

  const toggle = (id) => {
    if (normalized.includes(id)) onChange(normalized.filter((v) => v !== id));
    else onChange([...normalized, id]);
  };

  const grouped = (options || []).reduce((acc, o) => {
    const s = o.section || "Options";
    (acc[s] ||= []).push(o);
    return acc;
  }, {});

  return (
    <>
      <div data-testid={`${testidPrefix}-popup-${field.key}`}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-stone-200 bg-white hover:bg-stone-50 transition-colors text-left"
          data-testid={`${testidPrefix}-popup-open-${field.key}`}
        >
          <div>
            <p className="text-xs font-medium text-stone-700">{field.label}</p>
            {normalized.length === 0 ? (
              <p className="text-[10px] text-stone-400">Tap to add</p>
            ) : (
              <p className="text-[10px] text-stone-600">{normalized.length} selected</p>
            )}
          </div>
          <ChevronRight className="w-3 h-3 text-stone-400" />
        </button>
        {normalized.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {normalized.slice(0, 6).map((id) => {
              const opt = (options || []).find((o) => o.id === id);
              return (
                <span key={id} className="px-2 py-0.5 rounded-full text-[10px] bg-stone-100 text-stone-700">
                  {opt?.label || id}
                </span>
              );
            })}
            {normalized.length > 6 && (
              <span className="text-[10px] text-stone-400">+{normalized.length - 6} more</span>
            )}
          </div>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid={`${testidPrefix}-popup-dialog-${field.key}`}
        >
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-y-auto p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-semibold text-stone-900">{field.label}</h3>
                {field.help_text && <p className="text-[10px] text-stone-500 mt-0.5">{field.help_text}</p>}
              </div>
              <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            {options === null ? (
              <div className="py-10 text-center"><Loader2 className="w-4 h-4 animate-spin inline text-stone-400" /></div>
            ) : options.length === 0 ? (
              <p className="text-xs text-stone-400 text-center py-8">No options configured yet.</p>
            ) : (
              Object.entries(grouped).map(([section, opts]) => (
                <div key={section} className="mb-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">{section}</p>
                  <div className="grid grid-cols-1 gap-1">
                    {opts.map((opt) => {
                      const selected = normalized.includes(opt.id);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => toggle(opt.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg border-2 text-xs transition-colors ${
                            selected
                              ? "border-stone-900 bg-stone-900 text-white"
                              : "border-stone-200 bg-white hover:border-stone-400"
                          }`}
                          data-testid={`${testidPrefix}-popup-opt-${field.key}-${opt.id}`}
                        >
                          <div className="font-medium">{opt.label}</div>
                          {opt.help_text && (
                            <div className={`text-[10px] mt-0.5 ${selected ? "text-stone-300" : "text-stone-500"}`}>
                              {opt.help_text}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full mt-3 px-4 py-2 rounded-lg bg-stone-900 text-white text-xs font-medium"
              data-testid={`${testidPrefix}-popup-done-${field.key}`}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function ProductPicker({ field, value = [], onChange, testidPrefix, onProductPicked }) {
  const [products, setProducts] = useState(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [expandedProductId, setExpandedProductId] = useState(null); // for shade SKU drill-in
  const [addNewOpen, setAddNewOpen] = useState(false);
  const [addNewForm, setAddNewForm] = useState({ name: "", brand_id: "", brand_name: "", generic_shade_hint: "", image_url: "" });
  const [savingNew, setSavingNew] = useState(false);
  const [addNewError, setAddNewError] = useState("");
  // Iter 9.50+ R12 - brand search inside Add-new-product form. Reads from
  // /api/host/brands/visible (host-scope) with a fallback to /api/brands (public).
  // Adds a "+ Add new brand" path that POSTs to /api/host/brands/submit so the
  // brand lands in the user's library AND becomes visible to providers/admin.
  const [brands, setBrands] = useState(null);
  const [brandQuery, setBrandQuery] = useState("");
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const [addNewBrandOpen, setAddNewBrandOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const normalized = Array.isArray(value) ? value : value ? [value] : [];

  // Iter 9.49+ R6 - one popup at a time across the whole preference flow.
  useEffect(() => {
    if (open) {
      const closer = () => { setOpen(false); setExpandedProductId(null); };
      _registerActivePopup(closer);
      return () => _releaseActivePopup(closer);
    }
  }, [open]);

  useEffect(() => {
    if (!open || products !== null) return;
    let alive = true;
    (async () => {
      try {
        const params = new URLSearchParams();
        const f = field.product_filter || {};
        if (f.product_type) params.set("product_type", f.product_type);
        const token = localStorage.getItem("session_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await fetch(`${API}/master-products?${params.toString()}`, { headers });
        if (r.ok) {
          const d = await r.json();
          if (alive) setProducts(d.products || d || []);
        } else if (alive) setProducts([]);
      } catch {
        if (alive) setProducts([]);
      }
    })();
    return () => { alive = false; };
  }, [open, field.product_filter, products]);

  const toggle = (pid) => {
    if (normalized.includes(pid)) onChange(normalized.filter((v) => v !== pid));
    else {
      onChange([...normalized, pid]);
      // Iter 9.50+ R4: notify parent of the picked product object so it can
      // auto-fill linked fields (e.g. foundation_shade -> foundation_shade_generic).
      if (onProductPicked) {
        const product = (products || []).find((p) => {
          const productId = p.product_id || p.master_product_id || p.id || p.name;
          return productId === pid;
        });
        if (product) onProductPicked(product);
      }
    }
  };

  const submitNewProduct = async () => {
    if (!addNewForm.name.trim()) { setAddNewError("Product name is required"); return; }
    setSavingNew(true); setAddNewError("");
    try {
      const token = localStorage.getItem("session_token");
      const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const payload = {
        name: addNewForm.name.trim(),
        brand_id: addNewForm.brand_id || null,
        brand_name: addNewForm.brand_name.trim(),
        generic_shade_hint: addNewForm.generic_shade_hint.trim() || null,
        image_url: addNewForm.image_url.trim() || null,
        product_type: (field.product_filter || {}).product_type || null,
        source: "ugc",
      };
      const r = await fetch(`${API}/master-products/ugc`, { method: "POST", headers, body: JSON.stringify(payload) });
      if (!r.ok) { setAddNewError("Failed to add product. Try again."); setSavingNew(false); return; }
      const created = await r.json();
      const product = created.product || created;
      const newPid = product.product_id || product.master_product_id || product.id || product.name;
      // append to local list and auto-select
      setProducts((prev) => ([...(prev || []), product]));
      onChange([...normalized, newPid]);
      if (onProductPicked) onProductPicked(product);
      // reset
      setAddNewForm({ name: "", brand_id: "", brand_name: "", generic_shade_hint: "", image_url: "" });
      setAddNewOpen(false);
      setQuery("");
    } catch {
      setAddNewError("Network error. Try again.");
    } finally {
      setSavingNew(false);
    }
  };

  // Iter 9.50+ R12 - load visible brands (host's own + admin-published) once
  // the Add-new-product form is opened. Cached for the modal lifetime.
  useEffect(() => {
    if (!addNewOpen || brands !== null) return;
    let alive = true;
    (async () => {
      const token = localStorage.getItem("session_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const cat = (field.product_filter || {}).product_type ? "" : ""; // no category gate for now
      try {
        const r = await fetch(`${API}/host/brands/visible${cat ? `?category=${cat}` : ""}`, { headers });
        if (r.ok) {
          const d = await r.json();
          if (alive) setBrands(d.brands || []);
        } else {
          const r2 = await fetch(`${API}/brands`);
          const d2 = r2.ok ? await r2.json() : { brands: [] };
          if (alive) setBrands(d2.brands || []);
        }
      } catch {
        if (alive) setBrands([]);
      }
    })();
    return () => { alive = false; };
  }, [addNewOpen, field.product_filter, brands]);

  // Upload product image to Cloudinary via /upload/cloudinary - returns secure_url.
  const uploadProductImage = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "phygital/products/ugc");
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/upload/cloudinary`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (r.ok) {
        const d = await r.json();
        const url = d.secure_url || d.url || d.image_url;
        if (url) setAddNewForm((prev) => ({ ...prev, image_url: url }));
      }
    } catch { /* surfaced via UI - image stays blank */ }
    finally { setUploadingImage(false); }
  };

  const submitNewBrand = async () => {
    if (!newBrandName.trim()) return;
    try {
      const token = localStorage.getItem("session_token");
      const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const r = await fetch(`${API}/host/brands/submit`, { method: "POST", headers, body: JSON.stringify({ name: newBrandName.trim() }) });
      if (r.ok) {
        const d = await r.json();
        const b = d.brand;
        setBrands((prev) => ([...(prev || []), b]));
        setAddNewForm((prev) => ({ ...prev, brand_id: b.brand_id, brand_name: b.name }));
        setNewBrandName("");
        setAddNewBrandOpen(false);
        setBrandPickerOpen(false);
      }
    } catch { /* error stays silent; user can retry */ }
  };

  const filteredBrands = (brands || []).filter((b) => {
    if (!brandQuery) return true;
    return (b.name || "").toLowerCase().includes(brandQuery.toLowerCase());
  });

  const filtered = (products || []).filter((p) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (p.name || "").toLowerCase().includes(q) || (p.brand_name || "").toLowerCase().includes(q);
  });

  return (
    <>
      <div data-testid={`${testidPrefix}-product-${field.key}`}>
        <label className="text-xs font-medium text-stone-700">{field.label}</label>
        {field.help_text && <p className="text-[10px] text-stone-500">{field.help_text}</p>}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-1 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-stone-200 bg-white hover:bg-stone-50 text-left"
          data-testid={`${testidPrefix}-product-open-${field.key}`}
        >
          <span className="text-xs text-stone-500">
            {normalized.length === 0 ? "Tap to pick products" : `${normalized.length} selected`}
          </span>
          <ChevronRight className="w-3 h-3 text-stone-400" />
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid={`${testidPrefix}-product-dialog-${field.key}`}
        >
          <div
            className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-y-auto p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">{field.label}</h3>
              <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products or brands..."
              className="w-full px-3 py-1.5 mb-2 border border-stone-200 rounded-lg text-xs focus:outline-none focus:border-stone-400"
              data-testid={`${testidPrefix}-product-search-${field.key}`}
            />
            {addNewOpen ? (
              <div className="space-y-3" data-testid={`${testidPrefix}-add-new-form-${field.key}`}>
                <p className="text-[11px] text-stone-500">Add a new product to your kit. It&apos;ll be saved to your library and visible to your providers + admin.</p>

                {/* BRAND - search picker with "Add new brand" path - Iter 9.50+ R12 */}
                <div className="relative">
                  <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Brand</label>
                  <button
                    type="button"
                    onClick={() => setBrandPickerOpen(!brandPickerOpen)}
                    className="w-full mt-1 flex items-center justify-between px-3 py-2 border border-stone-200 rounded-lg text-xs hover:border-stone-400"
                    data-testid={`${testidPrefix}-brand-picker-toggle-${field.key}`}
                  >
                    <span className={addNewForm.brand_name ? "text-stone-900 font-medium" : "text-stone-400"}>
                      {addNewForm.brand_name || "Search or add a brand"}
                    </span>
                    <ChevronRight className={`w-3 h-3 text-stone-400 transition-transform ${brandPickerOpen ? "rotate-90" : ""}`} />
                  </button>
                  {brandPickerOpen && (
                    <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg p-2 space-y-2 max-h-64 overflow-y-auto" data-testid={`${testidPrefix}-brand-picker-${field.key}`}>
                      <div className="relative">
                        <Search className="absolute left-2 top-1.5 w-3 h-3 text-stone-400" />
                        <input
                          value={brandQuery}
                          onChange={(e) => setBrandQuery(e.target.value)}
                          placeholder="Search brands..."
                          className="w-full pl-7 pr-2 py-1.5 border border-stone-200 rounded-md text-xs"
                          data-testid={`${testidPrefix}-brand-search-${field.key}`}
                        />
                      </div>
                      {brands === null ? (
                        <div className="py-3 text-center"><Loader2 className="w-3 h-3 animate-spin inline text-stone-400" /></div>
                      ) : addNewBrandOpen ? (
                        <div className="space-y-2 p-1" data-testid={`${testidPrefix}-add-new-brand-form-${field.key}`}>
                          <p className="text-[10px] text-stone-500">This brand will be added to your library and reviewed by admins.</p>
                          <input
                            value={newBrandName}
                            onChange={(e) => setNewBrandName(e.target.value)}
                            placeholder="Brand name"
                            className="w-full px-2 py-1.5 border border-stone-200 rounded-md text-xs"
                            data-testid={`${testidPrefix}-new-brand-name-${field.key}`}
                            autoFocus
                          />
                          <div className="flex gap-1.5">
                            <button type="button" onClick={() => { setAddNewBrandOpen(false); setNewBrandName(""); }} className="flex-1 px-2 py-1.5 rounded-md border border-stone-200 text-[11px]" data-testid={`${testidPrefix}-new-brand-cancel-${field.key}`}>Cancel</button>
                            <button type="button" onClick={submitNewBrand} disabled={!newBrandName.trim()} className="flex-1 px-2 py-1.5 rounded-md bg-stone-900 text-white text-[11px] disabled:opacity-50" data-testid={`${testidPrefix}-new-brand-save-${field.key}`}>Add brand</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="space-y-0.5 max-h-40 overflow-y-auto">
                            {filteredBrands.slice(0, 30).map((b) => (
                              <button
                                key={b.brand_id}
                                type="button"
                                onClick={() => { setAddNewForm((prev) => ({ ...prev, brand_id: b.brand_id, brand_name: b.name })); setBrandPickerOpen(false); setBrandQuery(""); }}
                                className="w-full text-left px-2 py-1.5 rounded-md text-xs hover:bg-stone-100 flex items-center gap-2"
                                data-testid={`${testidPrefix}-brand-option-${field.key}-${b.brand_id}`}
                              >
                                {b.logo_url ? <img src={b.logo_url} alt="" className="w-4 h-4 rounded-full object-cover" /> : <div className="w-4 h-4 rounded-full bg-stone-200" />}
                                <span className="flex-1 truncate">{b.name}</span>
                                {b.pending_admin_review && <span className="text-[9px] text-amber-600 uppercase">yours</span>}
                              </button>
                            ))}
                            {filteredBrands.length === 0 && brandQuery && (
                              <p className="px-2 py-2 text-[11px] text-stone-400">No brand matches &quot;{brandQuery}&quot;.</p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => { setAddNewBrandOpen(true); setNewBrandName(brandQuery); }}
                            className="w-full mt-1 px-2 py-1.5 rounded-md border border-dashed border-stone-300 text-[11px] text-stone-600 hover:border-stone-500 hover:text-stone-900 flex items-center justify-center gap-1"
                            data-testid={`${testidPrefix}-add-new-brand-cta-${field.key}`}
                          >
                            <Plus className="w-3 h-3" />
                            {brandQuery ? `Add "${brandQuery}" as new brand` : "Add new brand"}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* PRODUCT NAME */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Product name</label>
                  <input value={addNewForm.name} onChange={(e) => setAddNewForm({ ...addNewForm, name: e.target.value })} placeholder="e.g. Pro Filt'r Foundation" className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg text-xs" data-testid={`${testidPrefix}-add-new-name-${field.key}`} />
                </div>

                {(field.product_filter || {}).product_type === "foundation" && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Closest generic shade</label>
                    <select value={addNewForm.generic_shade_hint} onChange={(e) => setAddNewForm({ ...addNewForm, generic_shade_hint: e.target.value })} className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg text-xs bg-white" data-testid={`${testidPrefix}-add-new-shade-${field.key}`}>
                      <option value="">Optional</option>
                      <option value="fair">Fair</option>
                      <option value="light">Light</option>
                      <option value="light_medium">Light medium</option>
                      <option value="medium">Medium</option>
                      <option value="medium_tan">Medium tan</option>
                      <option value="tan">Tan</option>
                      <option value="deep">Deep</option>
                      <option value="rich">Rich</option>
                    </select>
                  </div>
                )}

                {/* IMAGE - upload icon, no URL input - Iter 9.50+ R12 */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Image</label>
                  <div className="mt-1 flex items-start gap-2">
                    <label className="cursor-pointer flex-shrink-0">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadProductImage(f); }}
                        data-testid={`${testidPrefix}-product-image-upload-${field.key}`}
                      />
                      <div className={`w-20 h-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 ${uploadingImage ? "border-stone-400 bg-stone-50" : "border-stone-300 hover:border-stone-500 hover:bg-stone-50"} transition-colors`}>
                        {uploadingImage ? (
                          <Loader2 className="w-4 h-4 animate-spin text-stone-500" />
                        ) : addNewForm.image_url ? (
                          <img src={addNewForm.image_url} alt="" className="w-full h-full object-cover rounded-md" />
                        ) : (
                          <>
                            <Upload className="w-4 h-4 text-stone-500" />
                            <span className="text-[9px] text-stone-500 font-medium">Upload</span>
                          </>
                        )}
                      </div>
                    </label>
                    <p className="text-[10px] text-stone-500 leading-tight pt-1">Square image, &lt; 5 MB. We&apos;ll save it to your library. No URL needed.</p>
                  </div>
                </div>

                {addNewError && <p className="text-[11px] text-red-600" data-testid={`${testidPrefix}-add-new-error-${field.key}`}>{addNewError}</p>}
                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={() => { setAddNewOpen(false); setAddNewError(""); setBrandPickerOpen(false); }} className="flex-1 px-3 py-2 rounded-lg border border-stone-200 text-xs font-medium" data-testid={`${testidPrefix}-add-new-cancel-${field.key}`}>Cancel</button>
                  <button type="button" onClick={submitNewProduct} disabled={savingNew} className="flex-1 px-3 py-2 rounded-lg bg-stone-900 text-white text-xs font-medium disabled:opacity-60" data-testid={`${testidPrefix}-add-new-save-${field.key}`}>{savingNew ? "Saving..." : "Save product"}</button>
                </div>
              </div>
            ) : products === null ? (
              <div className="py-10 text-center"><Loader2 className="w-4 h-4 animate-spin inline text-stone-400" /></div>
            ) : filtered.length === 0 ? (
              <div className="py-8 text-center space-y-3">
                <p className="text-xs text-stone-400">
                  {(products || []).length === 0 ? "No products in the catalog yet." : "No matches for that search."}
                </p>
                <button
                  type="button"
                  onClick={() => { setAddNewOpen(true); setAddNewForm({ name: query, brand_id: "", brand_name: "", generic_shade_hint: "", image_url: "" }); }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-stone-900 text-white text-xs font-medium hover:bg-stone-800"
                  data-testid={`${testidPrefix}-add-new-empty-${field.key}`}
                >
                  <Plus className="w-3 h-3" />
                  {query ? `Add "${query}" as new` : "+ Add new product"}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {filtered.slice(0, 60).map((p) => {
                  const pid = p.product_id || p.master_product_id || p.id || p.name;
                  const selected = normalized.includes(pid);
                  const img = p.image_url || p.cover_image_url || p.thumbnail_url;
                  const shades = Array.isArray(p.shades) ? p.shades : [];
                  const hasShades = shades.length > 0;
                  const expanded = expandedProductId === pid;
                  return (
                    <div key={pid} className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          // Iter 9.49+ R6 - product cards with shades drill in
                          // to a shade-SKU picker; products without shades
                          // toggle as before.
                          if (hasShades) {
                            setExpandedProductId(expanded ? null : pid);
                          } else {
                            toggle(pid);
                          }
                        }}
                        className={`relative w-full text-left rounded-lg overflow-hidden border-2 transition-all ${
                          selected ? "border-stone-900 shadow-md" : "border-stone-200 hover:border-stone-400"
                        }`}
                        data-testid={`${testidPrefix}-product-item-${field.key}-${pid}`}
                      >
                        <div className="aspect-square bg-stone-100 flex items-center justify-center">
                          {img ? (
                            <img src={img} alt={p.name} className="w-full h-full object-cover" />
                          ) : (
                            <ImageIcon className="w-5 h-5 text-stone-400" />
                          )}
                        </div>
                        <div className="p-1.5">
                          <p className="text-[10px] text-stone-400">{p.brand_name || p.brand || ""}</p>
                          <p className="text-[11px] font-medium text-stone-800 truncate">{p.name}</p>
                          {hasShades && (
                            <p className="text-[9px] text-stone-500 mt-0.5">{shades.length} shades - tap to pick</p>
                          )}
                        </div>
                        {selected && !hasShades && (
                          <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-stone-900 flex items-center justify-center">
                            <Check className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </button>
                      {expanded && hasShades && (
                        <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg p-2 space-y-1 max-h-64 overflow-y-auto">
                          {shades.map((s) => {
                            const sid = `${pid}::${s.shade_id || s.shade_name}`;
                            const sSelected = normalized.includes(sid);
                            return (
                              <button
                                key={sid}
                                type="button"
                                onClick={(e) => { e.stopPropagation(); toggle(sid); }}
                                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-[11px] ${
                                  sSelected ? "bg-stone-900 text-white" : "bg-white hover:bg-stone-100 text-stone-800"
                                }`}
                                data-testid={`${testidPrefix}-shade-${field.key}-${sid}`}
                              >
                                {s.swatch_color && (
                                  <span className="w-3.5 h-3.5 rounded-full border border-stone-300 shrink-0"
                                        style={{ background: s.swatch_color }} />
                                )}
                                {s.image_url && !s.swatch_color && (
                                  <img src={s.image_url} alt={s.shade_name} className="w-4 h-4 rounded-full object-cover shrink-0" />
                                )}
                                <span className="flex-1 truncate">{s.shade_name || s.shade_id}</span>
                                {sSelected && <Check className="w-3 h-3" />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {!addNewOpen && filtered.length > 0 && (
              <button
                type="button"
                onClick={() => { setAddNewOpen(true); setAddNewForm({ name: query, brand_id: "", brand_name: "", generic_shade_hint: "", image_url: "" }); }}
                className="w-full mt-2 px-3 py-1.5 rounded-lg border border-dashed border-stone-300 text-stone-600 text-[11px] hover:border-stone-500 hover:text-stone-800 flex items-center justify-center gap-1"
                data-testid={`${testidPrefix}-add-new-link-${field.key}`}
              >
                <Plus className="w-3 h-3" />
                Can&apos;t find your product? Add new
              </button>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full mt-3 px-4 py-2 rounded-lg bg-stone-900 text-white text-xs font-medium"
              data-testid={`${testidPrefix}-product-done-${field.key}`}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function InspoGridPicker({ field, value = [], onChange, testidPrefix }) {
  const [open, setOpen] = useState(false);
  const [folders, setFolders] = useState(null);
  const [activeFolder, setActiveFolder] = useState(null);
  const normalized = Array.isArray(value) ? value : value ? [value] : [];

  useEffect(() => {
    if (open) {
      const closer = () => { setOpen(false); setActiveFolder(null); };
      _registerActivePopup(closer);
      return () => _releaseActivePopup(closer);
    }
  }, [open]);

  useEffect(() => {
    if (!open || folders !== null) return;
    let alive = true;
    (async () => {
      try {
        const cat = field.inspo_folder_category || field.field_id || "";
        const r = await fetch(`${API}/inspo-folders?category=${encodeURIComponent(cat)}`);
        if (r.ok) {
          const d = await r.json();
          if (alive) setFolders(d.folders || d || []);
        } else if (alive) setFolders([]);
      } catch {
        if (alive) setFolders([]);
      }
    })();
    return () => { alive = false; };
  }, [open, field, folders]);

  const toggle = (url) => {
    if (normalized.includes(url)) onChange(normalized.filter((v) => v !== url));
    else onChange([...normalized, url]);
  };

  return (
    <>
      <div data-testid={`${testidPrefix}-inspo-${field.key}`}>
        <label className="text-xs font-medium text-stone-700">{field.label}</label>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-1 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-stone-200 bg-white hover:bg-stone-50 text-left"
          data-testid={`${testidPrefix}-inspo-open-${field.key}`}
        >
          <span className="text-xs text-stone-500">
            {normalized.length === 0 ? "Tap to pick inspo" : `${normalized.length} selected`}
          </span>
          <ChevronRight className="w-3 h-3 text-stone-400" />
        </button>
        {normalized.length > 0 && (
          <div className="grid grid-cols-4 gap-1 mt-1.5">
            {normalized.slice(0, 8).map((u) => (
              <img key={u} src={u} alt="inspo" className="aspect-square object-cover rounded-md" />
            ))}
          </div>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => { setOpen(false); setActiveFolder(null); }}
          data-testid={`${testidPrefix}-inspo-dialog-${field.key}`}
        >
          <div
            className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-y-auto p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">
                {activeFolder ? activeFolder.name : field.label}
              </h3>
              <button onClick={() => { setOpen(false); setActiveFolder(null); }} className="text-stone-400 hover:text-stone-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            {folders === null ? (
              <div className="py-10 text-center"><Loader2 className="w-4 h-4 animate-spin inline text-stone-400" /></div>
            ) : activeFolder ? (
              <>
                <button
                  onClick={() => setActiveFolder(null)}
                  className="text-[11px] text-stone-500 hover:text-stone-700 mb-2"
                  data-testid={`${testidPrefix}-inspo-back-${field.key}`}
                >
                  Back to folders
                </button>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {(activeFolder.images || []).map((img) => {
                    const url = typeof img === "string" ? img : img.url || img.image_url;
                    if (!url) return null;
                    const selected = normalized.includes(url);
                    return (
                      <button
                        key={url}
                        type="button"
                        onClick={() => toggle(url)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                          selected ? "border-stone-900 shadow-md" : "border-stone-200 hover:border-stone-400"
                        }`}
                        data-testid={`${testidPrefix}-inspo-image-${field.key}`}
                      >
                        <img src={url} alt="inspo" className="w-full h-full object-cover" />
                        {selected && (
                          <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-stone-900 flex items-center justify-center">
                            <Check className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : folders.length === 0 ? (
              <p className="text-xs text-stone-400 text-center py-8">No inspo folders for this category yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {folders.map((f) => (
                  <button
                    key={f.folder_id || f.id || f.name}
                    type="button"
                    onClick={() => setActiveFolder(f)}
                    className="text-left rounded-lg overflow-hidden border-2 border-stone-200 hover:border-stone-400 transition-colors"
                    data-testid={`${testidPrefix}-inspo-folder-${field.key}-${f.folder_id || f.name}`}
                  >
                    <div className="aspect-square bg-stone-100 flex items-center justify-center">
                      {f.cover_image ? (
                        <img src={f.cover_image} alt={f.name} className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon className="w-5 h-5 text-stone-400" />
                      )}
                    </div>
                    <div className="px-2 py-1.5">
                      <p className="text-[11px] font-medium truncate">{f.name}</p>
                      <p className="text-[10px] text-stone-400">{(f.images || []).length} images</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => { setOpen(false); setActiveFolder(null); }}
              className="w-full mt-3 px-4 py-2 rounded-lg bg-stone-900 text-white text-xs font-medium"
              data-testid={`${testidPrefix}-inspo-done-${field.key}`}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </>
  );
}
